/**
 * Direct age standardization.
 *
 * With age-specific cases dᵢ, populations (or person-years) nᵢ and standard weights
 * wᵢ = Nᵢ / ΣN, the age-adjusted rate is ASR = Σ wᵢ · dᵢ/nᵢ — what the study population's
 * rate would be if it had the standard population's age structure.
 *
 * - Variance (Poisson counts): Var(ASR) = Σ wᵢ² · dᵢ / nᵢ².
 * - Interval: the gamma method of Fay & Feuer (1997, Stat Med 16:791–801), the one used
 *   by NCI's SEER*Stat and by `epitools::ageadjust.direct`: with y = ASR, v = Var(ASR)
 *   and wₘ = max(wᵢ/nᵢ),
 *     lower = Gamma⁻¹(α/2; shape y²/v, scale v/y),
 *     upper = Gamma⁻¹(1 − α/2; shape (y + wₘ)²/(v + wₘ²), scale (v + wₘ²)/(y + wₘ)).
 *   It keeps the interval positive and is exact when every weight/population ratio is equal.
 * - The crude rate gets the exact Poisson interval.
 */

import { chiSquareQuantile } from "@/lib/stats/distributions";
import { standardWeights } from "@/lib/tool-kit/stats-clinical/standard-populations";
import { poissonCountInterval } from "@/lib/tool-kit/stats-research/proportions";

export interface AgeGroupInput {
  label: string;
  cases: number;
  population: number;
}

export interface AgeGroupRate extends AgeGroupInput {
  /** Rate per `rateBase`. */
  rate: number;
  weight: number;
  /** wᵢ · rate: this group's contribution to the adjusted rate. */
  contribution: number;
}

export interface AdjustedRateResult {
  groups: AgeGroupRate[];
  totalCases: number;
  totalPopulation: number;
  crude: number;
  crudeLower: number;
  crudeUpper: number;
  adjusted: number;
  se: number;
  lower: number;
  upper: number;
  level: number;
  rateBase: number;
}

/** Gamma quantile through the chi-square: Gamma(shape k, scale θ) = θ · χ²(2k) / 2. */
function gammaQuantile(p: number, shape: number, scale: number): number {
  return (scale * chiSquareQuantile(p, 2 * shape)) / 2;
}

export type AdjustedRateProblem = "length" | "population" | "weights";

export function checkAgeGroups(
  groups: readonly AgeGroupInput[],
  standard: readonly number[]
): AdjustedRateProblem | null {
  if (groups.length !== standard.length || groups.length === 0) {
    return "length";
  }
  if (groups.some((g) => !(g.population > 0))) {
    return "population";
  }
  return standard.some((value) => !(value >= 0)) ||
    !(standard.reduce((s, v) => s + v, 0) > 0)
    ? "weights"
    : null;
}

export function directStandardization(
  groups: readonly AgeGroupInput[],
  standard: readonly number[],
  rateBase = 100_000,
  level = 0.95
): AdjustedRateResult | null {
  if (checkAgeGroups(groups, standard) !== null) {
    return null;
  }
  const weights = standardWeights(standard);
  let adjusted = 0;
  let variance = 0;
  let maxRatio = 0;
  const rows = groups.map((group, i) => {
    const rate = group.cases / group.population;
    adjusted += weights[i] * rate;
    variance += (weights[i] ** 2 * group.cases) / group.population ** 2;
    maxRatio = Math.max(maxRatio, weights[i] / group.population);
    return {
      ...group,
      rate: rate * rateBase,
      weight: weights[i],
      contribution: weights[i] * rate * rateBase,
    };
  });
  const totalCases = groups.reduce((sum, g) => sum + g.cases, 0);
  const totalPopulation = groups.reduce((sum, g) => sum + g.population, 0);
  const tail = (1 - level) / 2;
  const lower =
    adjusted === 0
      ? 0
      : gammaQuantile(tail, adjusted ** 2 / variance, variance / adjusted);
  const upper = gammaQuantile(
    1 - tail,
    (adjusted + maxRatio) ** 2 / (variance + maxRatio ** 2),
    (variance + maxRatio ** 2) / (adjusted + maxRatio)
  );
  const crudeInterval = poissonCountInterval(totalCases, level);
  return {
    groups: rows,
    totalCases,
    totalPopulation,
    crude: (totalCases / totalPopulation) * rateBase,
    crudeLower: (crudeInterval.lower / totalPopulation) * rateBase,
    crudeUpper: (crudeInterval.upper / totalPopulation) * rateBase,
    adjusted: adjusted * rateBase,
    se: Math.sqrt(variance) * rateBase,
    lower: lower * rateBase,
    upper: upper * rateBase,
    level,
    rateBase,
  };
}
